import React, { useState, useEffect } from 'react';
import { itemsAPI, categoriesAPI } from '../lib/api';
import { DuplicatePartWarning, useDuplicateCheck } from './DuplicatePartWarning';
import { ManualMergeModal } from './ManualMergeModal';
import { useAuth } from '../contexts/AuthContext';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Save, ExternalLink, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { UOM_OPTIONS, DEFAULT_UOM } from '../lib/uom';

export const EditItemModal = ({ 
  open, 
  onOpenChange, 
  item, 
  onSuccess 
}) => {
  const { isOwner } = useAuth();
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [manualMergeOpen, setManualMergeOpen] = useState(false);
  const [manualMergeItem, setManualMergeItem] = useState(null);
  const dupCheck = useDuplicateCheck({ context: 'catalogue' });
  const [editData, setEditData] = useState({
    name: '', sku: '', manufacturer_part_number: '',
    unit_cost: '', billable_price: '', reorder_threshold: '',
    category_ids: [], external_url: '', is_active: true, uom: DEFAULT_UOM,
  });

  useEffect(() => {
    if (open) fetchCategories();
  }, [open]);

  useEffect(() => {
    if (item && open) {
      setEditData({
        name: item.name || item.item_name || '',
        sku: item.sku || '',
        manufacturer_part_number: item.manufacturer_part_number || '',
        unit_cost: item.unit_cost?.toString() || '',
        billable_price: item.billable_price?.toString() || '',
        reorder_threshold: item.reorder_threshold?.toString() || '',
        category_ids: item.category_ids || [],
        external_url: item.external_url || '',
        is_active: item.is_active !== false,
        uom: item.uom || DEFAULT_UOM,
      });
      setTouched(false);
    }
  }, [item, open]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.list();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  };

  const updateField = (updates) => {
    setEditData(prev => ({ ...prev, ...updates }));
    setTouched(true);
  };

  const handleSave = async () => {
    if (!item) return;
    const itemId = item.id || item.item_id;
    if (!itemId) { toast.error('Cannot identify item to update'); return; }
    if (!editData.name.trim()) { toast.error('Item name is required'); return; }

    setSaving(true);
    try {
      await dupCheck.guardedCreate(
        {
          name: editData.name.trim(),
          sku: editData.sku || null,
          manufacturer_part_number: editData.manufacturer_part_number || null,
          category_ids: editData.category_ids || [],
          exclude_item_id: itemId,
        },
        () => _persistUpdate(itemId),
        async (existing) => {
          toast.info(`Existing part already exists: ${existing.name}. Edit cancelled.`);
          onOpenChange(false);
          if (onSuccess) onSuccess();
        },
      );
    } finally {
      setSaving(false);
    }
  };
  const _persistUpdate = async (itemId) => {
    try {
      await itemsAPI.update(itemId, {
        name: editData.name.trim(),
        sku: editData.sku || null,
        manufacturer_part_number: editData.manufacturer_part_number || null,
        unit_cost: editData.unit_cost ? parseFloat(editData.unit_cost) : null,
        billable_price: editData.billable_price ? parseFloat(editData.billable_price) : null,
        reorder_threshold: editData.reorder_threshold ? parseFloat(editData.reorder_threshold) : null,
        category_ids: editData.category_ids || [],
        external_url: editData.external_url || null,
        is_active: editData.is_active,
        uom: editData.uom,
      });
      toast.success('Item updated successfully');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleCategoryToggle = (categoryId, checked) => {
    const currentIds = editData.category_ids || [];
    if (checked) {
      updateField({ category_ids: [...currentIds, categoryId] });
    } else {
      updateField({ category_ids: currentIds.filter(id => id !== categoryId) });
    }
  };

  return (
    <>
      <DuplicatePartWarning {...dupCheck.dialogProps} />
      <AppDrawer
        open={open}
      onOpenChange={onOpenChange}
      title="Edit Item"
      description="Update item details, pricing, and categorization."
      size="md"
      isDirty={touched}
      data-testid="edit-item-drawer"
      footer={
        <>
          {isOwner && item && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const itemId = item.id || item.item_id;
                  const resp = await itemsAPI.get(itemId);
                  setManualMergeItem(resp.data);
                  setManualMergeOpen(true);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Could not open item');
                }
              }}
              data-testid="edit-modal-open-manual-merge"
              className="mr-auto"
            >
              <GitMerge className="w-4 h-4 mr-2" /> Merge with another item
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-modal-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !editData.name.trim()} data-testid="edit-modal-save">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />Save Changes</>
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Name */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Name *</Label>
          <Input value={editData.name} onChange={(e) => updateField({ name: e.target.value })}
            placeholder="Item name" className="h-12" data-testid="edit-modal-item-name" />
        </div>

        {/* SKU */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">SKU</Label>
          <Input value={editData.sku} onChange={(e) => updateField({ sku: e.target.value })}
            placeholder="e.g., PVC-050" className="h-12 font-mono" data-testid="edit-modal-item-sku" />
        </div>

        {/* Manufacturer Part # */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Manufacturer Part #</Label>
          <Input value={editData.manufacturer_part_number}
            onChange={(e) => updateField({ manufacturer_part_number: e.target.value })}
            placeholder="e.g., MFG-12345" className="h-12 font-mono" data-testid="edit-modal-item-mfg-part" />
        </div>

        {/* Unit of Measure */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Unit of Measure</Label>
          <Select value={editData.uom} onValueChange={(value) => updateField({ uom: value })}>
            <SelectTrigger className="h-12" data-testid="edit-modal-item-uom">
              <SelectValue placeholder="Select unit" />
            </SelectTrigger>
            <SelectContent>
              {UOM_OPTIONS.map((uom) => (
                <SelectItem key={uom.code} value={uom.code}>{uom.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">How this item is measured for quantity and pricing.</p>
        </div>

        {/* Categories */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Categories</Label>
          <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[48px] bg-muted/30">
            {categories.length > 0 ? categories.map(cat => (
              <label key={cat.id} className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={(editData.category_ids || []).includes(cat.id)}
                  onChange={(e) => handleCategoryToggle(cat.id, e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300" />
                <span className="text-sm">{cat.name}</span>
              </label>
            )) : (
              <span className="text-sm text-muted-foreground">No categories available</span>
            )}
          </div>
        </div>

        {/* Pricing Row */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Unit Cost ($)</Label>
            <Input type="number" step="0.01" min="0" value={editData.unit_cost}
              onChange={(e) => updateField({ unit_cost: e.target.value })}
              placeholder="0.00" className="h-12 font-mono" data-testid="edit-modal-item-cost" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Billable Price ($)</Label>
            <Input type="number" step="0.01" min="0" value={editData.billable_price}
              onChange={(e) => updateField({ billable_price: e.target.value })}
              placeholder="0.00" className="h-12 font-mono" data-testid="edit-modal-item-billable" />
          </div>
        </div>

        {/* Reorder Threshold */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Reorder Threshold</Label>
          <Input type="number" min="0" value={editData.reorder_threshold}
            onChange={(e) => updateField({ reorder_threshold: e.target.value })}
            placeholder="e.g., 5" className="h-12 font-mono" data-testid="edit-modal-item-threshold" />
        </div>

        {/* External URL */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Supply House Link</Label>
          <div className="flex gap-2">
            <Input type="url" value={editData.external_url}
              onChange={(e) => updateField({ external_url: e.target.value })}
              placeholder="https://supplier.com/item/123" className="flex-1 h-12"
              data-testid="edit-modal-item-url" />
            {editData.external_url && (
              <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0"
                onClick={() => window.open(editData.external_url, '_blank')} title="Open link">
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Active Toggle */}
        <div className="flex items-center gap-3 pt-2">
          <Switch checked={editData.is_active}
            onCheckedChange={(checked) => updateField({ is_active: checked })}
            data-testid="edit-modal-item-active" />
          <Label className="text-sm cursor-pointer">Active</Label>
        </div>
      </div>
      </AppDrawer>
      <ManualMergeModal
        open={manualMergeOpen}
        onOpenChange={(o) => {
          setManualMergeOpen(o);
          if (!o) setManualMergeItem(null);
        }}
        currentItem={manualMergeItem}
        onMergeSuccess={() => {
          setManualMergeOpen(false);
          setManualMergeItem(null);
          onOpenChange(false);
          if (onSuccess) onSuccess();
        }}
      />
    </>
  );
};
